import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { IconInfoCircle, IconCirclePlus } from '@tabler/icons';

import api from '../../../services/ApiManagement/Index';
import { useAuth } from '../../../hooks/auth';
import { useAnimalFilters } from '../../../hooks/animalFilters';
import { useOnlineStatus } from '../../../hooks/onlineStatus';

import BackHeader from '../../../components/BackHeader';
import SearchInput from '../../../components/Form/SearchInput';
import AnimalItem from '../../../components/Animal/Item';
import LoadingComponent from './components/LoadingComponent';

import { H2, NotFoundContainer } from '../../../components/Generic';
import { Container, Content, IconContainer, Row } from './styles';
import AnimalFiltersButton from '../../../components/AnimalFiltersButton';

const Search: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const [animals, setAnimals] = useState<any[]>([]);
  const [query, setQuery] = useState('');

  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalFound, setTotalFound] = useState(0);

  const { animalFilters, clearAnimalFilters } = useAnimalFilters();

  const history = useHistory();
  const { id_property }: any = useParams();
  const { user } = useAuth();
  const isOnline = useOnlineStatus();

  const [scrollRadio, setScrollRadio] = useState(null);
  const scrollObserve = useRef<any>();

  useEffect(() => {
    loadInfo();

    intersectionObserver.observe(scrollObserve.current);

    return () => {
      intersectionObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    handleSearch();
  }, [animalFilters]);

  useEffect(() => {
    if (hasMore && scrollRadio && isOnline && !loading) {
      loadMore();
    }
  }, [scrollRadio]);

  const intersectionObserver = new IntersectionObserver((entries: any) => {
    const radio = entries[0].intersectionRatio;
    setScrollRadio(radio);
  });

  const loadInfo = async () => {
    try {
      const propertyResponse = await api.get(
        `/propriedades/`,
        isOnline,
        'propriedades',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      if (propertyResponse.data.proprietario_id === user.id) {
        setCanEdit(true);
      } else {
        const userExists = propertyResponse.data.colaboradores.some(
          (el: any) => {
            return el.usuario_id === user.id;
          },
        );
        setCanEdit(userExists);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const loadMore = async () => {
    try {
      setLoadingMore(true);

      const response = await api.get(
        `/pesquisas/animais`,
        isOnline,
        'animais',
        {
          headers: {
            propriedade_id: id_property,
          },
          params: {
            ...animalFilters,
            pertencentes: !!animalFilters.pertencentes,
            key: query,
            limite: 10,
            pagina: page,
            cursor_id: animals[animals.length - 1]?.id || '',
          },
        },
      );
      setAnimals(animals.concat(response.data.animais));

      const total = animals.concat(response.data.animais).length;

      setHasMore(total < response.data.total);
      setPage(page + 1);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);

      const response = await api.get(
        `/pesquisas/animais`,
        isOnline,
        'animais',
        {
          headers: {
            propriedade_id: id_property,
          },
          params: {
            ...animalFilters,
            pertencentes: !!animalFilters.pertencentes,
            key: query,
            limite: 10,
            pagina: 1,
            cursor_id: '',
          },
        },
      );

      setAnimals(response.data.animais);

      if (isOnline) {
        const total = response.data.animais.length;
        setHasMore(total < response.data.total);
        setPage(2);

        setTotalFound(response.data.total);
      } else {
        setHasMore(false);
        setPage(2);

        setTotalFound(response.data.lenght);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <BackHeader title="Pesquisar animal">
        {canEdit && (
          <IconContainer
            onClick={() =>
              history.push(`/propriedade/${id_property}/animais/novo`)
            }
            id="edit-button"
          >
            <IconCirclePlus size={24} />
          </IconContainer>
        )}
      </BackHeader>

      <Content>
        <H2>Pesquisar</H2>
        <SearchInput
          placeholder="Digite o número, nome ou botom"
          onChange={(e: any) => setQuery(e.target.value)}
          onSubmit={handleSearch}
          style={{ marginBottom: 20 }}
        />

        <AnimalFiltersButton propertyId={id_property} />

        {!loading && totalFound > 0 ? (
          <div className="search-info">
            <p>
              <strong>{totalFound}</strong>{' '}
              {totalFound > 1 ? 'animais encontrados.' : 'animal encontrado.'}
            </p>

            <button
              onClick={() =>
                history.push(
                  `/propriedade/${id_property}/animais/pesquisar/detalhes`,
                )
              }
            >
              mais detalhes {'>'}
            </button>
          </div>
        ) : null}

        {loading ? (
          <LoadingComponent />
        ) : animals?.length > 0 ? (
          animals.map(animal => (
            <AnimalItem
              key={animal.id}
              imageUrl={
                animal.imagens?.length > 0 ? animal.imagens[0]?.image_url : ''
              }
              name={animal.nome}
              birth={animal.data_de_nascimento}
              number={animal.numero_de_identificacao}
              gender={animal.sexo}
              state={animal.estado}
              isPrivate={animal.privado}
              belongs={animal.pertencente}
              customStyle={{ marginTop: 20, cursor: 'pointer' }}
              onClick={() =>
                history.push(`/propriedade/${id_property}/animais/${animal.id}`)
              }
            />
          ))
        ) : (
          <NotFoundContainer>
            <IconInfoCircle size={24} />

            <strong>Nenhum animal encontrado.</strong>
          </NotFoundContainer>
        )}

        <div ref={scrollObserve} />

        {loadingMore && <LoadingComponent />}
      </Content>
    </Container>
  );
};

export default Search;
